import { AxiosError, type AxiosAdapter, type InternalAxiosRequestConfig } from 'axios';
import { transact } from './database.ts';

export const MOCK_MODE = true;
export const mockFailures: string[] = [];

export interface MockRequest {
  method: string;
  path: string;
  params: Record<string, unknown>;
  body: Record<string, unknown>;
  config: InternalAxiosRequestConfig;
}

export type MockHandler = (request: MockRequest) => unknown | Promise<unknown>;
const handlers = new Map<string, MockHandler>();
const patterns: { method: string; pattern: RegExp; handler: MockHandler }[] = [];
let pending: Promise<unknown> = Promise.resolve();
let requestGuard: ((request: MockRequest) => void) | undefined;
let responseGuard: ((request: MockRequest, response: unknown) => unknown) | undefined;
let activeRequest: MockRequest | undefined;

export function getActiveRequest(): MockRequest | undefined {
  return activeRequest;
}

export function setRequestGuard(guard: (request: MockRequest) => void): void {
  requestGuard = guard;
}

export function setResponseGuard(guard: (request: MockRequest, response: unknown) => unknown): void {
  responseGuard = guard;
}

export function registerPattern(method: string, pattern: RegExp, handler: MockHandler): void {
  patterns.push({ method: method.toUpperCase(), pattern, handler });
}

export function registerHandler(method: string, path: string, handler: MockHandler): void {
  handlers.set(`${method.toUpperCase()} ${path.toLowerCase()}`, handler);
}

export const mockAdapter: AxiosAdapter = async (config) => {
  const url = new URL(config.url ?? '', 'http://qcdoc.local');
  const method = (config.method ?? 'get').toUpperCase();
  const handler = handlers.get(`${method} ${url.pathname.toLowerCase()}`) ??
    patterns.find((entry) => entry.method === method && entry.pattern.test(url.pathname))?.handler;
  const response = { config, headers: {}, status: 200, statusText: 'OK', data: undefined as unknown };
  if (!handler) {
    mockFailures.push(`${method} ${url.pathname}`);
    response.status = 501;
    response.statusText = 'Not Implemented';
    response.data = { message: `Mock route not implemented: ${method} ${url.pathname}` };
    throw new AxiosError((response.data as { message: string }).message, 'ERR_MOCK_ROUTE', config, undefined, response);
  }
  try {
    const request: MockRequest = {
      method,
      path: url.pathname,
      params: { ...Object.fromEntries(url.searchParams), ...config.params },
      body: typeof config.data === 'string' ? JSON.parse(config.data) ?? {} : config.data ?? {},
      config,
    };
    const execute = () => transact(async () => {
      activeRequest = request;
      try {
        requestGuard?.(request);
        const result = await handler(request);
        return responseGuard ? responseGuard(request, result) : result;
      } finally {
        activeRequest = undefined;
      }
    });
    const operation = pending.then(execute, execute);
    pending = operation.catch(() => undefined);
    response.data = await operation;
    response.data = structuredClone(response.data);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Mock request failed.';
    mockFailures.push(`${method} ${url.pathname}: ${message}`);
    throw error;
  }
  return response;
};